/**
 * The editor's active format and monitor view (1.7.0, docs/dev/formats-1.7.0-build.md): which format the program
 * monitor, the inspector and the Reframe lane show ('primary' or a format id), and how the monitor shows the formats
 * (one at a time, the primary with the others' outlines, or all side by side). Remembered per sequence in this browser
 * (usePref), and back to the primary when the remembered format no longer exists.
 *
 * Editor.tsx makes the value with useFormatViewState and provides it (FormatViewProvider); everything under it reads it
 * with useFormatView. Without a provider useFormatView gives the primary, single view, and setters that do nothing,
 * so a component also works on its own (tests, the share page).
 */
import { createContext, useCallback, useContext, useMemo } from 'react';
import { PRIMARY_FORMAT, formatsOf, type FormatRef, type ResolvedFormat, type SequenceDocument } from '@prism/contracts';
import { usePref } from '../../lib/prefs';

/** 'single': the active format alone, WYSIWYG; 'guides': the primary with each format's crop outline; 'side': all formats side by side. */
export const FORMAT_VIEW_MODES = ['single', 'guides', 'side'] as const;
export type FormatViewMode = (typeof FORMAT_VIEW_MODES)[number];

export interface FormatViewState {
  /** The active format: 'primary' or a format id (always one the document has). */
  active: FormatRef;
  mode: FormatViewMode;
}

export interface FormatView extends FormatViewState {
  /** The document's formats, the primary first (sequence-format.ts formatsOf). */
  formats: ResolvedFormat[];
  /** The active format's details. */
  activeFormat: ResolvedFormat;
  setActive: (id: FormatRef) => void;
  setMode: (mode: FormatViewMode) => void;
}

/**
 * The formats' UI colours (1.7.0), by their place in formatsOf: 0 is the primary, 1–4 the formats in list order. UI
 * only. One palette for the format bar, Guides, Side by side, the Reframe lane, the inspector, the Format dialog and
 * the export list; styles.css defines the same values as --fmt-0…--fmt-4 (formatColor gives the variable).
 */
export const FORMAT_PALETTE = ['#8ab4ff', '#ffb347', '#4dd4ff', '#7dff9a', '#ff6fc8'] as const;
const paletteIndex = (index: number) => (index <= 0 ? 0 : ((index - 1) % (FORMAT_PALETTE.length - 1)) + 1);
/** A format's colour as a CSS variable (var(--fmt-N)), for styles. */
export const formatColor = (index: number): string => `var(--fmt-${paletteIndex(index)})`;
/** A format's colour as a hex value, where a CSS variable cannot be used (canvas). */
export const formatColorValue = (index: number): string => FORMAT_PALETTE[paletteIndex(index)]!;

/** The preference key a sequence's format view is remembered under. */
export const formatViewPrefKey = (sequenceId: string) => `editor.format.${sequenceId}`;

const DEFAULT_STATE: FormatViewState = { active: PRIMARY_FORMAT, mode: 'single' };

/** The remembered state kept valid for a document: an unknown format is the primary, an unknown mode 'single'. */
export function validFormatView(state: Partial<FormatViewState> | null | undefined, formats: ResolvedFormat[]): FormatViewState {
  const active = state?.active && formats.some((f) => f.id === state.active) ? state.active : PRIMARY_FORMAT;
  const mode = state?.mode && (FORMAT_VIEW_MODES as readonly string[]).includes(state.mode) ? state.mode : 'single';
  // Guides and Side by side need a format besides the primary.
  return { active, mode: formats.length > 1 ? mode : 'single' };
}

/** The format view of one sequence, for Editor.tsx to provide. */
export function useFormatViewState(sequenceId: string, doc: Pick<SequenceDocument, 'width' | 'height' | 'formats' | 'subtitleBottom' | 'fit'>): FormatView {
  const [stored, setStored] = usePref<FormatViewState>(formatViewPrefKey(sequenceId), DEFAULT_STATE);
  const formats = useMemo(() => formatsOf(doc), [doc.width, doc.height, doc.formats, doc.subtitleBottom, doc.fit]);
  const { active, mode } = validFormatView(stored, formats);
  const setActive = useCallback((id: FormatRef) => setStored({ active: id, mode }), [setStored, mode]);
  const setMode = useCallback((m: FormatViewMode) => setStored({ active, mode: m }), [setStored, active]);
  return useMemo(() => ({ active, mode, formats, activeFormat: formats.find((f) => f.id === active) ?? formats[0]!, setActive, setMode }), [active, mode, formats, setActive, setMode]);
}

const FormatViewContext = createContext<FormatView | null>(null);
/** Provides the editor's format view (Editor.tsx). */
export const FormatViewProvider = FormatViewContext.Provider;

const noop = () => {};

/**
 * The editor's format view. Without a provider: the primary of `doc` (when given), single view, no-op setters.
 */
export function useFormatView(doc?: Pick<SequenceDocument, 'width' | 'height' | 'formats' | 'subtitleBottom' | 'fit'>): FormatView {
  const ctx = useContext(FormatViewContext);
  if (ctx) return ctx;
  const formats = doc ? formatsOf(doc) : [{ id: PRIMARY_FORMAT, width: 1920, height: 1080, label: '16:9', aspect: '16:9', isPrimary: true, index: 0 }];
  return { ...DEFAULT_STATE, formats, activeFormat: formats[0]!, setActive: noop, setMode: noop };
}

/** The editor's edit function, as Timeline and Inspector take it: an op, and an undo coalescing key (keyCoalesce). */
export type EditorApply = (fn: (d: SequenceDocument) => SequenceDocument, coalesce?: string) => void;
